import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  Row,
  Table,
  TableColumns,
} from '@bp/ui-components';
import { useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../hooks/useConfirm';
import { loadingReducer, loadingReducerInitialState } from '../../../reducer/loadingReducer';
import { useClassesTable } from '../../Classes/hooks/useClassesTable';
import { ClassesTableType } from '../../Classes/types/ClassesTypes';
import { useTimetableStore } from '../../TimetableGrid/TimetableProvider';
import { useTimetableClasses } from '../hooks/useTimetableClasses';

type TimetableVersionClassesTableProps = {
  onRowSelection: (row: ClassesTableType, index: number) => void;
  onShowLessonClick: (uuid: string) => void;
  versionUuid: string;
  versionClassesUuids: string[];
  onRemove: (uuids: string[]) => void;
};

export const TimetableVersionClassesTable = ({
  onRowSelection,
  onShowLessonClick,
  versionUuid,
  versionClassesUuids,
  onRemove,
}: TimetableVersionClassesTableProps) => {
  const { t } = useTranslation();
  const store = useTimetableStore();

  const [loadingState] = useReducer(loadingReducer, loadingReducerInitialState);

  const { data, columns, tableSettings, tableSelection, tableSorting } = useClassesTable({
    columns: ['name', 'shortName', 'color'],
  });

  const versionClasses: ClassesTableType[] = data
    .filter((c) => versionClassesUuids.includes(c.uuid))
    .map((c) => ({ ...c, lessonCount: 0 }));

  const versionColumns: TableColumns<ClassesTableType>[] = [
    ...columns,
    {
      header: t('timetableVersion.lessonCount', { context: 'short' }),
      meta: { tooltip: t('timetableVersion.lessonCount', { context: 'long' }) },
      accessorKey: 'lessonCount',
      id: 'lessonCount',
      size: 75,
    },
  ];

  const { confirm, ConfirmationDialog } = useConfirm();

  const handleRemove = async (uuids: string[]) => {
    const res = await confirm({
      title: t('timetableVersion.removeClasses', { count: uuids.length }),
      message: t('timetableVersion.removeClassesDescription', { count: uuids.length }),
    });

    if (res) {
      tableSelection.onRowSelectionChange({ 0: true });
      onRemove(uuids);
    }
  };

  const { canBeRemoved } = useTimetableClasses(versionUuid);
  const readOnly = store.readonly;

  return (
    <>
      <Table<ClassesTableType>
        data={versionClasses}
        columns={versionColumns}
        {...tableSettings}
        {...tableSelection}
        {...tableSorting}
        showActionBar={false}
        minHeight={1050}
        lastColWidth='140px'
        lastCol={(row: Row<ClassesTableType>) => {
          return (
            <ButtonGroup loading={loadingState.some((ls) => ls.uuid === row.original.uuid)}>
              <Button hierarchy='secondary' onClick={() => onShowLessonClick(row.original.uuid)}>
                {t('timetableVersion.showLessons')}
              </Button>
              <Dropdown
                noPadding
                trigger={<Button hierarchy='secondary' icon={<DotsHorizontalIcon className='small' />} />}
              >
                <DropdownMenu
                  data={[
                    {
                      label: t('timetableVersion.editData'),
                      disabled: true,
                      onClick: () => {
                        console.log('edit clicked');
                      },
                    },
                    {
                      label: t('timetableVersion.test'),
                      disabled: true,
                    },
                    {
                      label: t('timetableVersion.remove'),
                      type: 'default',
                      color: 'error',
                      disabled: !canBeRemoved([row.original.uuid]) || readOnly,
                      onClick: async () => {
                        await handleRemove([row.original.uuid]);
                      },
                    },
                  ]}
                />
              </Dropdown>
            </ButtonGroup>
          );
        }}
        showRowHover
        showSelected
        showSelect={false}
        onRowClick={(_event, row) => {
          tableSelection.onRowSelectionChange({ [row.id]: true });
          onRowSelection(row.original, row.index);
          document.querySelector('#content')?.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      <ConfirmationDialog />
    </>
  );
};
